import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const ROLE_LABEL = { admin: "Administrateur", professeur: "Professeur", etudiant: "Étudiant" };
const ROLE_COLOR = { admin: "bg-accent", professeur: "bg-primary", etudiant: "bg-sky-600" };
const EMPTY = { name: "", username: "", email: "", password: "", role: "etudiant", filiere: "", promotion: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("Utilisateur modifié");
      } else {
        await api.post("/users", form);
        toast.success("Utilisateur créé");
      }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const edit = (u) => { setForm({ name: u.name, username: u.username || "", email: u.email, password: "", role: u.role, filiere: u.filiere || "", promotion: u.promotion || "" }); setEditId(u.id); setOpen(true); };
  const del = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); } };

  const shown = filter === "all" ? users : users.filter((u) => u.role === filter);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Administration</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Utilisateurs</h1>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="admin">Administrateurs</SelectItem>
              <SelectItem value="professeur">Professeurs</SelectItem>
              <SelectItem value="etudiant">Étudiants</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-user-btn"><Plus size={18} className="mr-2" /> Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouvel"} utilisateur</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nom complet</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                <div><Label>Identifiant (connexion)</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoCapitalize="none" autoCorrect="off" data-testid="user-username" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                <div><Label>Mot de passe {editId && "(laisser vide pour ne pas changer)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="etudiant">Étudiant</SelectItem>
                      <SelectItem value="professeur">Professeur</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1"><Label>Filière</Label><Input value={form.filiere} onChange={(e) => setForm({ ...form, filiere: e.target.value })} /></div>
                  <div className="flex-1"><Label>Promotion</Label><Input value={form.promotion} onChange={(e) => setForm({ ...form, promotion: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-user-btn">{editId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Identifiant</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Filière</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.username || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge className={`${ROLE_COLOR[u.role]} hover:${ROLE_COLOR[u.role]}`}>{ROLE_LABEL[u.role]}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{u.filiere || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => edit(u)} data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(u.id)} data-testid={`del-user-${u.id}`}><Trash size={16} className="text-accent" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
